import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { IoCubeOutline, IoAddOutline, IoSettingsOutline } from "react-icons/io5";
import { MdOutlineDelete } from "react-icons/md";
import {
  getSubcategories,
  addBOQToInclusionFormData,
} from "../../../../../services/budgetService";
import { requiresLeadTime } from "../../../../../constants/boq";
import { InlineAddItemRow } from "./InlineAddItemRow";
import { InlineEditBoqRow } from "./InlineEditBoqRow";
import { BOQItemCard } from "./BOQItemCard";
import { BOQItem } from "./BOQItem";
import { isBoqUncovered } from "../../../isBoqUncovered";
import { renderCurrencyIconColored } from "../../../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../../../utils/CommonFunction";

export const RoomCard = ({
  room,
  onOpenAddItemModal,
  onEditBoq,
  onDeleteBoq,
  onDeleteInclusion,
  onBudgetUpdated,
  onRoomExpandedChange,
  projectId,
  defaultExpanded = false,
  showCostFields = true,
  viewOnly = false,
  isDisable = false,
  inlineEditingBoqId,
  inlineEditingBoqInclusion,
  onSaveInlineEditBoq,
  onCancelInlineEditBoq,
  inlineEditSaving,
  isMobile = false,
  isTablet = false,
  project,
  isSiteVisit = false,
  isSiteEstimatesRoute = false,
  isReview = false,
  isExpanded = false,
}) => {
  const [expandedRoom, setExpandedRoom] = useState(() => !!defaultExpanded);
  const [expandedInclusions, setExpandedInclusions] = useState(() => {
    if (!defaultExpanded || !room?.inclusions) return {};
    return room.inclusions.reduce((acc, inc) => ({ ...acc, [inc.id]: true }), {});
  });
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState({});
  const [pendingItems, setPendingItems] = useState({});
  const [savingItems, setSavingItems] = useState({});

  const showUnitCostInput = !isSiteVisit;
  const onRoomExpandedChangeRef = useRef(onRoomExpandedChange);

  useEffect(() => {
    onRoomExpandedChangeRef.current = onRoomExpandedChange;
  }, [onRoomExpandedChange]);

  useEffect(() => {
    setExpandedRoom(!!defaultExpanded);
  }, [defaultExpanded]);

  useEffect(() => {
    if (defaultExpanded && room?.inclusions?.length) {
      setExpandedInclusions((prev) =>
        room.inclusions.reduce((acc, inc) => ({ ...acc, [inc.id]: true }), { ...prev }),
      );
    }
  }, [defaultExpanded, room?.inclusions]);

  useEffect(() => {
    if (!defaultExpanded) return;
    if (typeof onRoomExpandedChangeRef.current !== "function") return;
    onRoomExpandedChangeRef.current(room?.room_id, true, room?.room_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRoom = () => {
    const next = !expandedRoom;
    setExpandedRoom(next);
    if (typeof onRoomExpandedChangeRef.current === "function") {
      onRoomExpandedChangeRef.current(room?.room_id, next, room?.room_name);
    }
  };

  const fetchSubcategories = async (inclusionId, inclusionName) => {
    if (isExpanded) return;
    if (!inclusionName) return;
    try {
      setLoadingSubcategories((prev) => ({ ...prev, [inclusionId]: true }));
      const roomNameForApi =
        (room?.room_name && String(room.room_name).replace(/\s*\d+$/, "").trim()) || "";
      const data = await getSubcategories(inclusionName, project?.project_type || "", roomNameForApi || null);
      if (data?.success && data.subcategories) {
        const options = data.subcategories.map((item) => ({
          label: item.subcategory_name,
          value: item.subcategory_name,
        }));
        setSubcategoriesMap((prev) => ({ ...prev, [inclusionId]: options }));
      } else {
        setSubcategoriesMap((prev) => ({ ...prev, [inclusionId]: [] }));
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubcategoriesMap((prev) => ({ ...prev, [inclusionId]: [] }));
    } finally {
      setLoadingSubcategories((prev) => ({ ...prev, [inclusionId]: false }));
    }
  };

  const toggleInclusion = async (inclusionId, inclusionName) => {
    const isExpanding = !expandedInclusions[inclusionId];
    setExpandedInclusions((prev) => ({ ...prev, [inclusionId]: isExpanding }));
    if (isExpanded) return;
    if (isExpanding && !subcategoriesMap[inclusionId]) {
      await fetchSubcategories(inclusionId, inclusionName);
    }
  };

  const getNextItemNameForSubcategory = (inclusionId, subcategoryValue) => {
    const escaped = String(subcategoryValue).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`^${escaped} \\d+$`);
    const sameBase = Object.values(pendingItems).filter(
      (item) =>
        item.inclusionId === inclusionId &&
        (item.item_name === subcategoryValue || regex.test(item.item_name)),
    );
    return sameBase.length === 0 ? subcategoryValue : `${subcategoryValue} ${sameBase.length}`;
  };

  const handleSubcategoryClick = (inclusionId, subcategory) => {
    const uniqueKey = `${inclusionId}_${subcategory.value}_${Date.now()}`;
    const itemName = getNextItemNameForSubcategory(inclusionId, subcategory.value);
    setPendingItems((prev) => ({
      ...prev,
      [uniqueKey]: {
        id: uniqueKey,
        inclusionId,
        item_name: itemName,
        subcategory: subcategory.value,
        boq_type: "Materials",
        quantity: "",
        days: "",
        material_unit: null,
        supply_apply: null,
        specification: "",
        unit_cost: "",
        lead_time: "",
      },
    }));
  };

  const handleUpdatePendingItem = (key, updates) => {
    setPendingItems((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  };

  const handleCancelPendingItem = (key) => {
    setPendingItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSavePendingItem = async (key, inclusion) => {
    const pendingItem = pendingItems[key];
    if (!pendingItem) return;
    if (!pendingItem.item_name) {
      toast.error("Item name is required");
      return;
    }
    if (pendingItem.boq_type === "Materials") {
      const qty = pendingItem.quantity || "";
      if (qty === "" || isNaN(qty) || parseInt(qty) < 0) {
        toast.error("Quantity is required and must be 0 or a positive number");
        return;
      }
    }
    if (pendingItem.boq_type === "Activities") {
      const d = pendingItem.days || "";
      if (d === "" || isNaN(d) || parseInt(d) < 0) {
        toast.error("Days is required and must be 0 or a positive number");
        return;
      }
    }
    if (pendingItem.boq_type === "Materials" && !pendingItem.material_unit) {
      toast.error("Material Unit is required");
      return;
    }
    if (!pendingItem.supply_apply) {
      toast.error("Supply/Apply is required");
      return;
    }
    // Lead Time mandatory when Supply / Supply and Apply (reno-web rule).
    if (requiresLeadTime(pendingItem.supply_apply)) {
      const lt = String(pendingItem.lead_time ?? "").trim();
      if (lt === "") {
        toast.error("Please enter Lead Time (days)");
        return;
      }
      if (!/^\d+$/.test(lt)) {
        toast.error("Lead Time must be a positive integer");
        return;
      }
    }
    if (!pendingItem.specification) {
      toast.error("Specification is required");
      return;
    }
    if (showUnitCostInput && isSiteEstimatesRoute) {
      const uc = pendingItem.unit_cost;
      if (uc === "" || uc == null) {
        toast.error("Unit cost is required");
        return;
      }
      if (Number(uc) === 0) {
        toast.error("Enter a valid cost");
        return;
      }
    } else if (
      showUnitCostInput &&
      pendingItem.unit_cost !== "" &&
      pendingItem.unit_cost != null &&
      Number(pendingItem.unit_cost) === 0
    ) {
      toast.error("Enter a valid cost");
      return;
    }

    setSavingItems((prev) => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append("name", pendingItem.item_name);
      formData.append("type", pendingItem.boq_type);
      formData.append("subcategory", pendingItem.subcategory);
      formData.append("supply_type", pendingItem.supply_apply);
      formData.append("specification", pendingItem.specification);
      formData.append(
        "material_unit",
        pendingItem.boq_type === "Materials" ? pendingItem.material_unit ?? "" : "",
      );
      if (pendingItem.boq_type === "Materials") {
        formData.append("quantity", pendingItem.quantity || "");
        formData.append("days", "");
      } else {
        formData.append("days", pendingItem.days || "");
        formData.append("quantity", "");
      }
      formData.append("room_id", room.room_id);
      formData.append("inclusion_name", inclusion.name);
      if (showUnitCostInput && pendingItem.unit_cost != null && pendingItem.unit_cost !== "") {
        formData.append("unit_cost", String(pendingItem.unit_cost).trim());
      }
      // Append lead_time only when applicable and non-empty — backend
      // expects an integer or the field absent (treated as null).
      if (
        requiresLeadTime(pendingItem.supply_apply) &&
        pendingItem.lead_time != null &&
        String(pendingItem.lead_time).trim() !== ""
      ) {
        formData.append("lead_time", String(pendingItem.lead_time).trim());
      }

      // No inline image attachment — image is uploaded after save from
      // the read-only BOQItem/BOQItemCard.
      const data = await addBOQToInclusionFormData(projectId, inclusion.id, formData);
      if (data?.success !== false) {
        toast.success(`Successfully added ${pendingItem.item_name} to ${inclusion.name}`);
        handleCancelPendingItem(key);
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    } finally {
      setSavingItems((prev) => ({ ...prev, [key]: false }));
    }
  };

  const getPendingItemsForInclusion = (inclusionId) =>
    Object.entries(pendingItems)
      .filter(([key]) => key.startsWith(`${inclusionId}_`))
      .map(([key, item]) => ({ key, ...item }));

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* Room Header */}
      <div style={{ padding: 24, borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <IoCubeOutline style={{ width: 24, height: 24, color: "#4b5563" }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 500 }}>{room.room_name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 14, color: "#4b5563", marginTop: 4 }}>
                <span>{room.area ? `${room.area} sqm` : "- sqm"}</span>
                <span>Height: {room.height ? `${room.height}m` : "- m"}</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {showCostFields && (
              <span
                style={{
                  padding: "2px 8px",
                  fontSize: 12,
                  fontWeight: 500,
                  background: "#dcfce7",
                  color: "#166534",
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {renderCurrencyIconColored("black-small")}
                {formattedAmountWithoutAed(room.room_total)}
              </span>
            )}
            <button
              onClick={toggleRoom}
              style={{
                padding: 8,
                color: "#9ca3af",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <svg
                style={{
                  width: 20,
                  height: 20,
                  transform: expandedRoom ? "none" : "rotate(180deg)",
                  transition: "transform 0.2s",
                }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 15l6-6 6 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Inclusions */}
      {expandedRoom && (
        <div>
          {(room.inclusions || []).map((inclusion) => {
            const inclusionPendingItems = getPendingItemsForInclusion(inclusion.id);
            const subcategories = subcategoriesMap[inclusion.id] || [];
            const isLoadingSubcats = loadingSubcategories[inclusion.id];
            const isIncExpanded = !!expandedInclusions[inclusion.id];
            return (
              <div
                key={inclusion.id}
                style={{ padding: "24px 24px 24px 48px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <IoSettingsOutline style={{ width: 20, height: 20, color: "#4b5563" }} />
                    <div>
                      <h4 style={{ fontSize: 18, fontWeight: 500, color: "#0D0D0E", margin: 0 }}>
                        {inclusion.name}
                      </h4>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 14, color: "#4b5563" }}>
                          {inclusion.boq_count} items
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {showCostFields && (
                      <span
                        style={{
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 500,
                          background: "#ffedd5",
                          color: "#9a3412",
                          borderRadius: 999,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        {renderCurrencyIconColored("black-small")}
                        {formattedAmountWithoutAed(inclusion.inclusion_amount)}
                      </span>
                    )}
                    {/* {!viewOnly && !isDisable && onDeleteInclusion && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteInclusion(inclusion, room);
                        }}
                        style={{
                          padding: 8,
                          color: "#9ca3af",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                        title="Delete inclusion"
                      >
                        <MdOutlineDelete style={{ width: 20, height: 20 }} />
                      </button>
                    )} */}
                    <button
                      onClick={() => toggleInclusion(inclusion.id, inclusion.name)}
                      style={{
                        padding: 8,
                        color: "#9ca3af",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <svg
                        style={{
                          width: 20,
                          height: 20,
                          transform: isIncExpanded ? "rotate(180deg)" : "none",
                          transition: "transform 0.2s",
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {isIncExpanded && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {(inclusion.boqs || []).map((boq) => {
                      const uncovered = isBoqUncovered(boq);
                      return !isMobile &&
                        inlineEditingBoqId === boq.id &&
                        inlineEditingBoqInclusion?.id === inclusion.id ? (
                        <InlineEditBoqRow
                          key={`edit-${boq.id}`}
                          boq={boq}
                          inclusion={inclusion}
                          projectId={projectId}
                          onSave={() => onSaveInlineEditBoq && onSaveInlineEditBoq()}
                          onCancel={onCancelInlineEditBoq}
                          isSaving={inlineEditSaving}
                          showCostFields={showCostFields}
                          onBudgetUpdated={onBudgetUpdated}
                          isSiteVisit={isSiteVisit}
                          isSiteEstimatesRoute={isSiteEstimatesRoute}
                          isUncovered={uncovered && !isSiteVisit}
                        />
                      ) : isMobile ? (
                        <BOQItemCard
                          key={boq.id}
                          boq={boq}
                          projectId={projectId}
                          inclusionId={inclusion.id}
                          showCostFields={showCostFields}
                          showUnitCostInput={!isSiteVisit && !viewOnly}
                          isSiteVisit={isSiteVisit}
                          isSiteEstimatesRoute={isSiteEstimatesRoute}
                          onBudgetUpdated={onBudgetUpdated}
                          onEdit={viewOnly ? undefined : () => onEditBoq && onEditBoq(boq, inclusion, room)}
                          onDelete={viewOnly ? undefined : () => onDeleteBoq && onDeleteBoq(boq, inclusion.id, room)}
                          disabled={false}
                          isDisable={isDisable}
                          isUncovered={uncovered && !isSiteVisit}
                        />
                      ) : (
                        <BOQItem
                          key={boq.id}
                          boq={boq}
                          showCostFields={showCostFields}
                          onEdit={viewOnly ? undefined : () => onEditBoq && onEditBoq(boq, inclusion, room)}
                          onDelete={viewOnly ? undefined : () => onDeleteBoq && onDeleteBoq(boq, inclusion.id, room)}
                          onBudgetUpdated={onBudgetUpdated}
                          disabled={false}
                          isDisable={isDisable}
                          isUncovered={uncovered && !isSiteVisit}
                        />
                      );
                    })}

                    {/* Mobile add button */}
                    {/* {!viewOnly && !isDisable && isMobile && onOpenAddItemModal && (
                      <button
                        onClick={() => onOpenAddItemModal(inclusion, room)}
                        style={{
                          width: "100%",
                          marginTop: 16,
                          padding: "8px 16px",
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#2563eb",
                          background: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        <IoAddOutline
                          style={{ width: 16, height: 16, display: "inline", marginRight: 8, verticalAlign: "middle" }}
                        />
                        Add Item to {inclusion.name}
                      </button>
                    )} */}

                    {/* Desktop inline add */}
                    {!viewOnly &&
                      !isDisable &&
                      !isMobile &&
                      inclusionPendingItems.map((item) => (
                        <InlineAddItemRow
                          key={item.key}
                          pendingItem={item}
                          onUpdate={(updates) => handleUpdatePendingItem(item.key, updates)}
                          onSave={() => handleSavePendingItem(item.key, inclusion)}
                          onCancel={() => handleCancelPendingItem(item.key)}
                          isSaving={savingItems[item.key]}
                          showUnitCostInput={showUnitCostInput}
                        />
                      ))}

                    {/* Subcategories */}
                    {/* {!viewOnly && !isDisable && !isMobile && (
                      <div style={{ marginTop: 16 }}>
                        {!isExpanded && (
                          <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 8 }}>Add items:</div>
                        )}
                        {isLoadingSubcats ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#6b7280" }}>
                            <span>Loading subcategories...</span>
                          </div>
                        ) : subcategories.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {subcategories.map((subcat) => (
                              <button
                                key={subcat.value}
                                onClick={() => handleSubcategoryClick(inclusion.id, subcat)}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: 12,
                                  fontWeight: 500,
                                  borderRadius: 8,
                                  border: "1px solid #d1d5db",
                                  background: "#fff",
                                  color: "#4b5563",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  cursor: "pointer",
                                }}
                              >
                                <IoAddOutline style={{ width: 12, height: 12 }} />
                                {subcat.label}
                              </button>
                            ))}
                          </div>
                        ) : !isExpanded ? (
                          <div style={{ fontSize: 14, color: "#9ca3af" }}>No subcategories available</div>
                        ) : null}
                      </div>
                    )} */}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoomCard;
